/**
 * Resolves book cover image URL from extracted PDF covers.
 * Cover mapping is fetched from /assets/cover-images/cover-mapping.json
 * (generated by scripts/extract-pdf-covers.js).
 *
 * @param {Object} book - { author, title }
 * @param {Object} mapping - { "author-slug|title-slug": "covers/xxx.png" }
 * @returns {string|null} Absolute path for cover or null
 */
function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function mappingKey(author, title) {
  return `${slugify(author)}|${slugify(title)}`;
}

const VITE_BASE_URL = (import.meta?.env?.BASE_URL || '/').replace(/\/$/, '');

// Reuse tokenization across many getCoverUrl calls (BookGrid renders many cards).
const titleTokensCache = new Map();
function tokenizeSlug(slug) {
  if (titleTokensCache.has(slug)) return titleTokensCache.get(slug);
  const tokens = String(slug || '')
    .split('-')
    .filter(Boolean);
  titleTokensCache.set(slug, tokens);
  return tokens;
}

function jaccardSimilarity(aTokens, bTokens) {
  if (!aTokens.length || !bTokens.length) return 0;
  const a = new Set(aTokens);
  const b = new Set(bTokens);
  let inter = 0;
  for (const x of a) if (b.has(x)) inter++;
  const union = a.size + b.size - inter;
  return union ? inter / union : 0;
}
function withBase(pathname) {
  // pathname must start with "/"
  return `${VITE_BASE_URL}${pathname}`;
}

/**
 * Get cover URL for a book from the cover mapping.
 * Supports both legacy keys (author-slug|title-slug) and new filename-based keys (slugified basename).
 *
 * @param {{ author?: string, title?: string }} book
 * @param {Record<string, string>} mapping - from cover-mapping.json
 * @returns {string|null} e.g. "/assets/cover-images/covers/author_title.png" or null
 */
export function getCoverUrl(book, mapping) {
  if (!mapping || !book) return null;

  const author = book.author || book.auteur_nom || '';
  const title = book.title || book.titre || '';
  const authorSlug = slugify(author);
  const titleSlug = slugify(title);

  // 1) Exact match: legacy "author-slug|title-slug" key
  const exactKey = mappingKey(author, title);
  let rel = mapping[exactKey];
  if (rel) return withBase(`/assets/cover-images/${rel}`);

  // 2) New format: slugified filename keys (e.g., "my-book", "author-title")
  rel = mapping[titleSlug];
  if (rel) return withBase(`/assets/cover-images/${rel}`);

  const authorTitleSlug = authorSlug ? `${authorSlug}-${titleSlug}` : titleSlug;
  rel = mapping[authorTitleSlug];
  if (rel) return withBase(`/assets/cover-images/${rel}`);

  const titleAuthorSlug = authorSlug ? `${titleSlug}-${authorSlug}` : titleSlug;
  rel = mapping[titleAuthorSlug];
  if (rel) return withBase(`/assets/cover-images/${rel}`);

  // 3) Fallback: match by title prefix (legacy keys with "|" and new slugified keys)
  const titleTokens = tokenizeSlug(titleSlug);
  let bestRel = null;
  let bestScore = 0;

  for (const [key, value] of Object.entries(mapping)) {
    const mapTitleSlug = key.includes('|') ? key.split('|')[1] : key;
    if (!mapTitleSlug) continue;

    if (mapTitleSlug === titleSlug || mapTitleSlug.startsWith(`${titleSlug}-`)) {
      return withBase(`/assets/cover-images/${value}`);
    }

    const mapTokens = tokenizeSlug(mapTitleSlug);
    const jac = jaccardSimilarity(titleTokens, mapTokens);
    const prefixBonus =
      mapTitleSlug.startsWith(`${titleSlug}-`) || titleSlug.startsWith(`${mapTitleSlug}-`)
        ? 0.25
        : 0;
    const score = jac + prefixBonus;
    if (score > bestScore) {
      bestScore = score;
      bestRel = value;
    }
  }

  if (bestRel && bestScore >= 0.35) {
    return withBase(`/assets/cover-images/${bestRel}`);
  }

  return null;
}

const FALLBACK_PLACEHOLDER = 'https://placehold.co/600x900/e8f5e9/2e7d32?text=';
/**
 * Get cover URL with fallback placeholder.
 */
export function getCoverUrlWithFallback(book, mapping, existingUrl) {
  const extracted = getCoverUrl(book, mapping);
  if (extracted) return extracted;
  if (existingUrl && !existingUrl.startsWith('https://placehold.co')) return existingUrl;
  const title = (book?.title || book?.titre || 'Livre').toString().slice(0, 30);
  return `${FALLBACK_PLACEHOLDER}${encodeURIComponent(title)}`;
}

let cachedMapping = null;
let fetchPromise = null;

/**
 * Fetch cover mapping JSON (cached).
 * @returns {Promise<Record<string, string>>}
 */
export async function fetchCoverMapping() {
  if (cachedMapping) return cachedMapping;
  if (fetchPromise) return fetchPromise;
  fetchPromise = fetch(withBase('/assets/cover-images/cover-mapping.json'))
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}))
    .then((m) => {
      cachedMapping = m;
      return m;
    });
  return fetchPromise;
}
